import React, { createContext, useState } from "react";
import useJobsStore from "Store/useJobsStore";

export const JobDataContext = createContext();

/**
 * JobDataProvider
 *
 * Keeps V1 components working via React Context while simultaneously syncing
 * every state change into `useJobsStore` (Zustand).
 *
 * V2 components bypass the context entirely and subscribe to `useJobsStore`
 * with fine-grained selectors, avoiding unnecessary re-renders when unrelated
 * state changes.
 *
 * Bridge pattern:
 *  V1 setter call → updates React state (re-renders context consumers)
 *                 → also calls useJobsStore.getState().setXxx() (updates Zustand)
 *  V2 read        → useJobsStore(state => state.xxx) (only re-renders on that slice)
 */
export const JobDataProvider = ({ children }) => {

    const [selectedJob, _setSelectedJob] = useState(null);
    const [isDrawerOpen, _setIsDrawerOpen] = useState(false);
    const [selectedJdTab, _setSelectedJdTab] = useState('job-description');
    const [uploadJdResponseId, _setUploadJdResponseId] = useState('');
    const [createJdResponseId, _setCreateJdResponseId] = useState('');
    const [jdActionSelection, _setJdActionSelection] = useState('');

    const [reloadRoles, _setReloadRoles] = useState(false);
    const [waitingforResponse, _setWaitingforResponse] = useState(false);
    const [llmresponse, _setLlmresponse] = useState();
    const [reviseJdProgress, _setReviseJdProgress] = useState(false);
    const [compareViewSelection, _setCompareViewSelection] = useState("");
    const [jobData, _setJobData] = useState([]);
    const [isDiffCheckerV2DisplayJD, _setIsDiffCheckerV2DisplayJD] = useState(false);

    // ── Sync wrappers: update both Context state AND Zustand store ───────────

    const setSelectedJob = (job) => {
        _setSelectedJob(job);
        useJobsStore.getState().setSelectedJob(job);
    };

    const setIsDrawerOpen = (open) => {
        _setIsDrawerOpen(open);
        useJobsStore.getState().setIsDrawerOpen(open);
    };

    const toggleDrawer = () => {
        _setIsDrawerOpen((prev) => {
            const next = !prev;
            useJobsStore.getState().setIsDrawerOpen(next);
            return next;
        });
    };

    // Handle the back button click
    const handleBack = (create = false) => {
        setSelectedJob(null);
        if (create === true) {
            toggleDrawer();
        }
    };

    const setSelectedJdTab = (tab) => {
        _setSelectedJdTab(tab);
        useJobsStore.getState().setSelectedJdTab(tab);
    };

    const setUploadJdResponseId = (id) => {
        _setUploadJdResponseId(id);
    };

    const setCreateJdResponseId = (id) => {
        _setCreateJdResponseId(id);
    };

    const setJdActionSelection = (sel) => {
        _setJdActionSelection(sel);
        useJobsStore.getState().setJdActionSelection(sel);
    };

    const setReloadRoles = (val) => { _setReloadRoles(val); };
    const setWaitingforResponse = (val) => { _setWaitingforResponse(val); };
    const setLlmresponse = (val) => { _setLlmresponse(val); };

    const setReviseJdProgress = (val) => {
        _setReviseJdProgress(val);
        useJobsStore.getState().setReviseJdProgress(val);
    };

    const setCompareViewSelection = (val) => {
        _setCompareViewSelection(val);
        useJobsStore.getState().setCompareViewSelection(val);
    };

    const setJobData = (data) => {
        _setJobData(data);
        useJobsStore.getState().setJobData(data);
    };

    const setIsDiffCheckerV2DisplayJD = (val) => {
        _setIsDiffCheckerV2DisplayJD(val);
        useJobsStore.getState().setIsDiffCheckerV2DisplayJD(val);
    };

    return (
        <JobDataContext.Provider
            value={{
                selectedJob,
                setSelectedJob,
                isDrawerOpen,
                setIsDrawerOpen,
                toggleDrawer,
                handleBack,
                selectedJdTab,
                setSelectedJdTab,
                uploadJdResponseId,
                setUploadJdResponseId,
                createJdResponseId,
                setCreateJdResponseId,
                reloadRoles,
                setReloadRoles,
                waitingforResponse,
                setWaitingforResponse,
                llmresponse,
                setLlmresponse,
                jdActionSelection,
                setJdActionSelection,
                setReviseJdProgress,
                reviseJdProgress,
                setCompareViewSelection,
                compareViewSelection,
                setJobData,
                jobData,
                isDiffCheckerV2DisplayJD,
                setIsDiffCheckerV2DisplayJD
            }}
        >
            {children}
        </JobDataContext.Provider>
    );
};
